import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Search, Filter, Plus, Users, ArrowUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import NewLeadModal from "./leads/NewLeadModal";
import { getLeads, getSalespeople } from "../apiHelper";
import LeadItem from "./leads/LeadItem";
const Leads = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth();
  const [leads, setLeads] = useState({});
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [salespersonFilter, setSalespersonFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [salespeople, setSalespeople] = useState([]);

  // Fetch salespeople on component mount
  useEffect(() => {
    getAccessTokenSilently().then((t) => {
      getSalespeople(t).then((salespeople) => {
        setSalespeople(salespeople);
      });
    });
  }, []);

  useEffect(() => {
    getAccessTokenSilently().then((t) => {
      const params = { page: 1, keyword: search, sortBy, sortOrder };
      if (channelFilter && channelFilter !== "all") {
        params.channel = channelFilter;
      }
      if (salespersonFilter && salespersonFilter !== "all") {
        params.salesperson = salespersonFilter;
      }
      getLeads(t, params).then((l) => {
        setLeads(l);
      });
    });
  }, [search, channelFilter, salespersonFilter, sortBy, sortOrder]);

  // Debounce effect for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(inputValue);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 1) {
      if (leads.hasNextPage) {
        getAccessTokenSilently().then((t) =>
          getLeads(t, {
            page: leads.nextPage,
            keyword: search,
            channel: channelFilter !== "all" ? channelFilter : undefined,
            salesperson:
              salespersonFilter !== "all" ? salespersonFilter : undefined,
            sortBy,
            sortOrder,
          }).then((newResponse) => {
            setLeads({
              ...newResponse,
              docs: [...leads.docs, ...newResponse?.docs],
            });
          })
        );
      }
    }
  };

  const handleLeadUpdate = (updatedLead) => {
    setLeads((prevLeads) => ({
      ...prevLeads,
      docs: prevLeads.docs.map((lead) =>
        lead._id === updatedLead._id ? updatedLead : lead
      ),
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-3 lg:p-6 flex flex-col">
      <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8 w-full overflow-hidden flex flex-col flex-1">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Leads
            </h1>
            <p className="text-sm lg:text-base text-gray-600">
              Manage and track your leads
            </p>
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 px-4 py-2 lg:px-6 lg:py-3 rounded-xl text-sm lg:text-base"
          >
            <Plus className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
            <span className="hidden sm:inline">Add Lead</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Search, Filter, and Sort Controls */}
        <div className="space-y-4 lg:space-y-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 lg:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 lg:h-5 lg:w-5" />
                <Input
                  placeholder="Search leads..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="pl-10 lg:pl-12 h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base"
                />
              </div>
            </div>
            <div className="w-full lg:w-48 xl:w-56">
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base">
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-0 shadow-xl">
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="demo">Demo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full lg:w-48 xl:w-56">
              <Select
                value={salespersonFilter}
                onValueChange={setSalespersonFilter}
              >
                <SelectTrigger className="h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base">
                  <SelectValue placeholder="All Salespeople" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-0 shadow-xl">
                  <SelectItem value="all">All Salespeople</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {salespeople.map((salesperson) => (
                    <SelectItem
                      key={salesperson.externalId}
                      value={salesperson.externalId}
                    >
                      {salesperson.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full lg:w-48 xl:w-56">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4 text-gray-500" />
                    <SelectValue placeholder="Sort by" />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-xl border-0 shadow-xl">
                  <SelectItem value="created">Created Date</SelectItem>
                  <SelectItem value="leadStatus">Lead Status</SelectItem>
                  <SelectItem value="lastCalled">Last Called</SelectItem>
                  <SelectItem value="scheduledNextCall">
                    Next Call Scheduled
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full lg:w-32 xl:w-36">
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="h-10 lg:h-12 rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200 shadow-sm text-sm lg:text-base">
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-0 shadow-xl">
                  <SelectItem value="desc">Newest First</SelectItem>
                  <SelectItem value="asc">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Filters and Sort */}
          <div className="flex flex-wrap items-center gap-3">
            {channelFilter && channelFilter !== "all" && (
              <div className="flex items-center gap-3">
                <Filter className="h-4 w-4 text-blue-600" />
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 px-4 py-2 rounded-lg shadow-sm"
                >
                  Channel: {channelFilter}
                  <button
                    onClick={() => setChannelFilter("all")}
                    className="ml-2 hover:text-blue-800 transition-colors"
                  >
                    ×
                  </button>
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Leads List */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-200/50 rounded-2xl flex flex-col flex-1">
          <CardHeader className="pb-4 lg:pb-6">
            <CardTitle className="flex items-center gap-2 lg:gap-3 text-lg lg:text-xl">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Users className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <span className="text-base lg:text-xl">Lead List</span>
              {leads.docs && (
                <Badge
                  variant="outline"
                  className="ml-2 lg:ml-3 bg-blue-50 text-blue-700 border-blue-200 px-2 py-1 lg:px-3 lg:py-1 rounded-lg text-xs lg:text-sm"
                >
                  {leads.docs.length} leads
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto px-3 lg:px-6 pb-4 lg:pb-6 w-full">
              <div className="space-y-3 lg:space-y-4 w-full">
                {leads.docs?.map((l) => (
                  <LeadItem
                    lead={l}
                    key={l._id}
                    onLeadUpdate={handleLeadUpdate}
                    salespeople={salespeople}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <NewLeadModal
          open={modalOpen}
          setOpen={setModalOpen}
          setLeads={setLeads}
        />
      </div>
    </div>
  );
};

export default Leads;
